import React, { Ref, forwardRef, useCallback, useImperativeHandle, useState, KeyboardEvent } from "react";
import _ from "lodash";
import { ICellEditorParams } from "ag-grid-community";
import { MenuItem, MenuList, Paper, Theme, Typography, makeStyles, Checkbox } from "@material-ui/core";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { cancelEvent } from "containers/legacy-cart/helpers/common";
import { IOption } from "constants/types";
import theme from "styles/theme";

const DEFAULT_DROPDOWN_HEIGHT = 150;

type OptionValue = string | number | boolean;

export type MultiSelectEditorProps = ICellEditorParams & {
    value: OptionValue[];
    options: IOption<OptionValue>[];
    dropdownHeight?: number;
};

export default forwardRef(MultiSelectEditor);

function MultiSelectEditor(props: MultiSelectEditorProps, ref: Ref<{ getValue: () => any }>) {
    const { value, options, formatValue, colDef } = props;
    const { dropdownHeight = DEFAULT_DROPDOWN_HEIGHT } = props;

    const [selectedOptions, setSelectedOptions] = useState<Set<OptionValue>>(new Set(formatValue(value)));

    const handleSelect = useCallback((option: OptionValue) => {
        setSelectedOptions((selectedOptions) => {
            const newSelectedOptions = new Set(selectedOptions);
            if (newSelectedOptions.has(option)) {
                newSelectedOptions.delete(option);
            } else {
                newSelectedOptions.add(option);
            }

            return newSelectedOptions;
        });
    }, []);

    const handleValueClick = useCallback((value: OptionValue) => () => handleSelect(value), [handleSelect]);
    const handleEnterDown = useCallback(
        (value: OptionValue) => (e: KeyboardEvent<HTMLLIElement>) => {
            if (e.key === KEYBOARD_KEYS.ENTER) {
                cancelEvent(e);
                handleSelect(value);
            }
        },
        [handleSelect],
    );

    useImperativeHandle(ref, () => ({ getValue: () => Array.from(selectedOptions) }));

    const classes = useStyles({ width: colDef.width, height: dropdownHeight });
    return (
        <Paper className={classes.wrapper}>
            <MenuList autoFocus={true}>
                {options.map(({ value, label }) => (
                    <MenuItem
                        key={_.toString(value)}
                        value={_.toString(value)}
                        selected={selectedOptions.has(value)}
                        onClick={handleValueClick(value)}
                        onKeyDown={handleEnterDown(value)}
                        className={classes.item}
                    >
                        <Checkbox className={classes.checkbox} checked={selectedOptions.has(value)} color="primary" />
                        <Typography variant="inherit" noWrap={true}>
                            {label}
                        </Typography>
                    </MenuItem>
                ))}
            </MenuList>
        </Paper>
    );
}

const useStyles = makeStyles<Theme, { width: number; height: number }>({
    wrapper: ({ width, height }) => ({
        width,
        maxHeight: height,
        overflow: "auto",
    }),
    item: {
        fontFamily: "Lato, sans-serif",
        fontSize: 14,
    },
    checkbox: {
        color: theme.palette.grey,
        "&.Mui-checked": {
            color: theme.palette.mint,
        },
        "& .MuiSvgIcon-root": {
            fontSize: 18,
        },
    },
});
